import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../ui/Dialog';
import { apiClient, type ManagedClusterKind } from '../../lib/api-client';
import { Trash2, Plus, Pencil, Search, Loader2, ChevronDown, Check } from 'lucide-react';
import { Skeleton } from '../ui/Skeleton';
import { Button } from '../ui/Button';
import { DropdownMenu } from '../ui/DropdownMenu';

interface ManageSecretsDialogProps {
  open: boolean;
  onClose: () => void;
  tenantCode: string;
  instanceName: string;
  cloudProvider: string;
  clusterKind?: ManagedClusterKind;
}

interface K8sSecret {
  name: string;
  namespace: string;
  type: string;
  dataKeys: string[];
}

export function ManageSecretsDialog({ open, onClose, tenantCode, instanceName, cloudProvider, clusterKind }: ManageSecretsDialogProps) {
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [selectedNamespace, setSelectedNamespace] = useState('');
  const [secrets, setSecrets] = useState<K8sSecret[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'list' | 'create' | 'update'>('list');
  const [selectedSecret, setSelectedSecret] = useState<K8sSecret | null>(null);
  const [secretName, setSecretName] = useState('');
  const [secretType, setSecretType] = useState('Opaque');
  const [secretData, setSecretData] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open) {
      loadNamespaces();
    }
  }, [open, tenantCode, instanceName, cloudProvider]);

  useEffect(() => {
    if (selectedNamespace && mode === 'list') {
      loadSecrets();
    }
  }, [selectedNamespace, mode]);

  const loadNamespaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getOpenshiftNamespaces(tenantCode, instanceName, cloudProvider, clusterKind);
      // Filter out openshift-* namespaces
      const filtered = data.namespaces.filter((ns: string) => !ns.startsWith('openshift-'));
      setNamespaces(filtered);
      if (filtered.length > 0 && !selectedNamespace) {
        setSelectedNamespace(filtered[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load namespaces');
    } finally {
      setLoading(false);
    }
  };

  const loadSecrets = async () => {
    if (!selectedNamespace) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getOpenshiftSecrets(tenantCode, instanceName, cloudProvider, selectedNamespace, clusterKind);
      setSecrets(data.secrets || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load secrets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setMode('create');
    setSecretName('');
    setSecretType('Opaque');
    setSecretData({ key: '' });
    setError(null);
  };

  const handleUpdate = (secret: K8sSecret) => {
    setMode('update');
    setSelectedSecret(secret);
    setSecretName(secret.name);
    setSecretType(secret.type);
    // Initialize with existing keys but empty values (for security)
    const initialData: Record<string, string> = {};
    secret.dataKeys.forEach(key => {
      initialData[key] = '';
    });
    setSecretData(initialData);
    setError(null);
  };

  const handleSave = async () => {
    if (!secretName.trim()) {
      setError('Secret name is required');
      return;
    }

    // Check if at least one key-value pair is provided
    const hasData = Object.entries(secretData).some(([key, value]) => key.trim() && value.trim());
    if (!hasData) {
      setError('At least one key-value pair is required');
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      await apiClient.saveOpenshiftSecret(
        tenantCode,
        instanceName,
        cloudProvider,
        selectedNamespace,
        secretName,
        secretType,
        secretData,
        clusterKind,
      );
      
      // Reset form and go back to list mode
      setSecretName('');
      setSecretType('Opaque');
      setSecretData({});
      setSelectedSecret(null);
      setMode('list');
    } catch (err: any) {
      setError(err.message || 'Failed to save secret');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setMode('list');
      setSecretName('');
      setSecretType('Opaque');
      setSecretData({});
      setSelectedSecret(null);
      setSelectedNamespace('');
      setError(null);
      setSearchQuery('');
      onClose();
    }
  };

  const handleCancel = () => {
    setMode('list');
    setSecretName('');
    setSecretType('Opaque');
    setSecretData({});
    setSelectedSecret(null);
    setError(null);
  };

  const addDataField = () => {
    const newKey = `key${Object.keys(secretData).length + 1}`;
    setSecretData({ ...secretData, [newKey]: '' });
  };

  const updateDataKey = (oldKey: string, newKey: string) => {
    const newData = { ...secretData };
    newData[newKey] = newData[oldKey];
    delete newData[oldKey];
    setSecretData(newData);
  };

  const updateDataValue = (key: string, value: string) => {
    setSecretData({ ...secretData, [key]: value });
  };

  const removeDataField = (key: string) => {
    const newData = { ...secretData };
    delete newData[key];
    setSecretData(newData);
  };

  // Filter secrets based on search query
  const filteredSecrets = secrets.filter(secret =>
    secret.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'list' && `Manage Secrets - ${instanceName} (${tenantCode})`}
            {mode === 'create' && `Create New Secret - ${instanceName} (${tenantCode})`}
            {mode === 'update' && `Update Secret - ${instanceName} (${tenantCode})`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'list' && 'Manage Kubernetes secrets in non-OpenShift namespaces'}
            {mode === 'create' && `Create a new secret in namespace: ${selectedNamespace}`}
            {mode === 'update' && `Update secret in namespace: ${selectedNamespace}`}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {mode === 'list' && (
            <>
            <div className="mb-4 space-y-3">
              {/* Namespace Selector */}
              <div>
                <label htmlFor="namespace" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Namespace
                </label>
                {/* Desktop native select */}
                <select
                  id="namespace"
                  value={selectedNamespace}
                  onChange={(e) => setSelectedNamespace(e.target.value)}
                  className="hidden md:block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  {namespaces.map((ns) => (
                    <option key={ns} value={ns}>
                      {ns}
                    </option>
                  ))}
                </select>

                {/* Mobile bottom-sheet picker */}
                <div className="md:hidden">
                  <DropdownMenu
                    className="w-full"
                    align="left"
                    trigger={
                      <button
                        type="button"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex items-center justify-between"
                      >
                        <span className="min-w-0 truncate">{selectedNamespace || 'Select namespace'}</span>
                        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                      </button>
                    }
                    items={namespaces.map((ns) => ({
                      label: ns,
                      icon: ns === selectedNamespace ? <Check className="w-4 h-4" /> : undefined,
                      onClick: () => setSelectedNamespace(ns),
                      className: 'font-mono',
                    }))}
                  />
                </div>
              </div>

              {selectedNamespace && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    onClick={handleCreate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    <Plus className="w-4 h-4" />
                    Create New Secret
                  </button>

                  <div className="flex-1 relative w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search secrets..."
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Scrollable area for secrets list */}
            {selectedNamespace && (
              <div className="overflow-y-auto max-h-[500px]">
                {loading ? (
                  <div className="space-y-2 py-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : filteredSecrets.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    {searchQuery ? `No secrets matching "${searchQuery}"` : 'No secrets found in this namespace'}
                  </div>
                ) : (
                  <>
                    {/* Desktop table */}
                    <div className="hidden md:block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                            Secret Name
                            {searchQuery && (
                              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                ({filteredSecrets.length} of {secrets.length})
                              </span>
                            )}
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                            Keys
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredSecrets.map((secret) => (
                          <tr key={secret.name} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">
                              {secret.name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {secret.type}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                              {secret.dataKeys.length} key(s)
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleUpdate(secret)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                  title="Update secret"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-2">
                      {filteredSecrets.map((secret) => (
                        <div
                          key={secret.name}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white/0"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
                                {secret.name}
                              </div>
                              <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                {secret.type}
                              </div>
                            </div>
                            <button
                              onClick={() => handleUpdate(secret)}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors flex-shrink-0"
                              title="Update secret"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="mt-3 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                            <span>Keys</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {secret.dataKeys.length} key(s)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}
            </>
          )}

          {(mode === 'create' || mode === 'update') && (
            <div className="space-y-4">
            <div>
              <label htmlFor="secretName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Secret Name
              </label>
              <input
                id="secretName"
                type="text"
                value={secretName}
                onChange={(e) => setSecretName(e.target.value)}
                placeholder="e.g., my-secret"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                disabled={saving || mode === 'update'}
              />
            </div>

            <div>
              <label htmlFor="secretType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type
              </label>
              {/* Desktop native select */}
              <select
                id="secretType"
                value={secretType}
                onChange={(e) => setSecretType(e.target.value)}
                className="hidden md:block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                disabled={saving || mode === 'update'}
              >
                <option value="Opaque">Opaque</option>
                <option value="kubernetes.io/service-account-token">Service Account Token</option>
                <option value="kubernetes.io/dockercfg">Docker Config</option>
                <option value="kubernetes.io/dockerconfigjson">Docker Config JSON</option>
                <option value="kubernetes.io/basic-auth">Basic Auth</option>
                <option value="kubernetes.io/ssh-auth">SSH Auth</option>
                <option value="kubernetes.io/tls">TLS</option>
              </select>

              {/* Mobile bottom-sheet picker */}
              <div className="md:hidden">
                <DropdownMenu
                  className="w-full"
                  align="left"
                  trigger={
                    <button
                      type="button"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex items-center justify-between disabled:opacity-50"
                      disabled={saving || mode === 'update'}
                    >
                      <span className="min-w-0 truncate">{secretType}</span>
                      <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                    </button>
                  }
                  items={[
                    { label: 'Opaque', icon: secretType === 'Opaque' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('Opaque') },
                    { label: 'Service Account Token', icon: secretType === 'kubernetes.io/service-account-token' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('kubernetes.io/service-account-token') },
                    { label: 'Docker Config', icon: secretType === 'kubernetes.io/dockercfg' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('kubernetes.io/dockercfg') },
                    { label: 'Docker Config JSON', icon: secretType === 'kubernetes.io/dockerconfigjson' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('kubernetes.io/dockerconfigjson') },
                    { label: 'Basic Auth', icon: secretType === 'kubernetes.io/basic-auth' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('kubernetes.io/basic-auth') },
                    { label: 'SSH Auth', icon: secretType === 'kubernetes.io/ssh-auth' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('kubernetes.io/ssh-auth') },
                    { label: 'TLS', icon: secretType === 'kubernetes.io/tls' ? <Check className="w-4 h-4" /> : undefined, onClick: () => setSecretType('kubernetes.io/tls') },
                  ]}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Data (Key-Value Pairs)
                </label>
                <button
                  onClick={addDataField}
                  disabled={saving}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Field
                </button>
              </div>

              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {Object.entries(secretData).map(([key, value], index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={key}
                      onChange={(e) => updateDataKey(key, e.target.value)}
                      placeholder="key"
                      className="w-1/3 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                      disabled={saving || (mode === 'update' && selectedSecret?.dataKeys.includes(key))}
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateDataValue(key, e.target.value)}
                      placeholder="value"
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                      disabled={saving}
                    />
                    <button
                      onClick={() => removeDataField(key)}
                      disabled={saving}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {mode === 'update' && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Enter values only for keys you want to update. Leave blank to keep existing values.
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}
          </div>
          )}
        </DialogBody>

        <DialogFooter>
          {mode === 'list' ? (
            <Button variant="secondary" onClick={handleClose} disabled={saving}>
              Close
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={saving || !secretName.trim()}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  mode === 'create' ? 'Create Secret' : 'Update Secret'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

